import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import {
  withTrackProps,
  createLogEntryPayload,
} from "@dpdgroupuk/react-event-tracker";
import { pick } from "ramda";

import {
  isRouteReadyOrOpen,
  isSaveRouteEnabled,
  isScheduleRequired,
  isRouteStatusInBuild,
} from "../../../store/selectors/route";
import {
  getMoveToButtonDisabledState,
  getSelectedParcelsCount,
  getSelectedPreparedStops,
  getStopFilters,
  getPreparedStops,
  getStopTransferDisabledState,
  getParcelTransferDisabledState,
  isKingColumnDisabled,
  getRemoveParcelsDisabledState,
} from "../../../store/selectors/ui/routeDetail";
import * as routeDetailActions from "../../../store/actions/ui/routeDetail";
import {
  onChangeBulkStop,
  updateKingParcel,
} from "../../../store/actions/stop";
import useParcelDetailsTableProps from "../hooks/useParcelDetailsTableProps";
import RouteManagement from "../../../components/RouteManagement";
import MergeStopsModal from "../../../components/modals/MergeStops";
import SplitStopModal from "../../../components/modals/SplitStop";
import ModalControlled from "../../../components/ModalControlled";
import { wrapPromise } from "../../../utils/promise";
import useMergeStopsModalData from "../hooks/useMergeStopsModalData";
import { showNotification } from "../../../store/actions/notifications";
import { NotificationType } from "../../Notifier";
import { startScheduleRoute } from "../../../store/actions/schedule";
import SelectByCriteriaModal from "../../selectByCriteria";
import {
  useBodyParcelDetailsCellStyles,
  useHeaderParcelDetailsCellStyles,
} from "../../../components/styles/tablesCells";
import useSplitStopModalData from "../hooks/useSplitStopModalData";
import { DISMISS } from "../../../constants/strings";
import StopMoveTo from "../../../components/modals/StopMoveTo";
import { isMergeDisabled, isSplitDisabled } from "../../../models/routeDetail";
import {
  moveSelectedStops,
  saveRoute,
  setScheduleRequired,
} from "../../../store/actions/route";
import TransferTo from "../../../components/modals/TransferTo";
import modalTypes from "../../../enums/modalTypes";
import {
  ROUTE_MANAGEMENT,
  STOP_PARCELS_LIST,
} from "../../../constants/analytics";
import { useOverlay } from "../../../components/Overlay";
import TransferSummary from "../../../components/modals/TransferSummary";
import useSummaryModalData from "../hooks/useSummaryModalData";
import useRemoveParcelsModalData from "../hooks/useRemoveParcelsModalData";
import RemoveParcels from "../../../components/modals/RemoveParcels";

const RouteManagementContainer = ({
  onSelectMultipleClick,
  handleBackClick,
  onParcelRequestClick,
  scheduleRequired,
  selectedCount,
  selectedStops,
  isKingDisabled,
  criteria,
  isMoveToDisabled,
  isStopTransferDisabled,
  isParcelTransferDisabled,
  saveRouteEnabled,
  routeStatusInBuild,
  preparedStops,
  routeReadyOrOpen,
  clearSelectedResetOrder,
  handleParcelSelect,
  onChangeBulk,
  onScheduleClick,
  onOptimiseClick,
  onSaveClick,
  onRemoveCriteria,
  showNotificationMessage,
  moveSelectedStops,
  startScheduleRoute,
  onUpdateKingParcel,
  onSplitClickActionId,
  onClickAddNewCriteriaActionId,
  onMergeClickActionId,
  onClickMoveToActionId,
  onParcelNumberClick,
  handleOnLoad,
  setScheduleRequired,
  isRemoveDisabled,
}) => {
  const selectByCriteriaModalRef = useRef();
  const stopMoveToModalRef = useRef();
  const transferToModalRef = useRef();
  const { showOverlayWhile } = useOverlay();

  const parcelDetailsTableProps = useParcelDetailsTableProps({
    modifiers: {
      onUpdateKingParcel,
      onParcelRequestClick,
      onParcelNumberClick,
    },
    headerCellClasses: useHeaderParcelDetailsCellStyles(),
    bodyCellClasses: useBodyParcelDetailsCellStyles(),
    isKingParcelColumnDisabled: isKingDisabled,
  });

  useEffect(() => {
    handleOnLoad();
  }, [handleOnLoad]);

  useEffect(() => {
    if (criteria.length && !selectedStops.length) {
      showNotificationMessage();
    }
  }, [criteria, showNotificationMessage, selectedStops.length]);

  const { mergeStopsModalRef, openMergeStopsModal } = useMergeStopsModalData();
  const { splitStopModalRef, openSplitStopsModal } = useSplitStopModalData();
  const { summaryModalRef, openSummaryModal } = useSummaryModalData();
  const {
    removeParcelsModalRef,
    openRemoveParcelsModal,
  } = useRemoveParcelsModalData();

  const handleOnRemoveClick = useCallback(async () => {
    // TODO add action Id
    const [result = {}, canceled] = await openRemoveParcelsModal();
    if (canceled) return;
    const { optimise, transferOnly } = result;
    if (!transferOnly) {
      startScheduleRoute(optimise);
    } else {
      setScheduleRequired(true);
      openSummaryModal({ isTransfer: false });
    }
  }, [
    openRemoveParcelsModal,
    startScheduleRoute,
    openSummaryModal,
    setScheduleRequired,
  ]);

  const handleOnSplitClick = useCallback(() => {
    onSplitClickActionId();
    openSplitStopsModal();
  }, [onSplitClickActionId, openSplitStopsModal]);

  const handleOnMergeClick = useCallback(() => {
    onMergeClickActionId();
    openMergeStopsModal();
  }, [onMergeClickActionId, openMergeStopsModal]);

  const handleSelectByCriteria = useCallback(() => {
    onClickAddNewCriteriaActionId();
    wrapPromise(selectByCriteriaModalRef.current.showModal());
  }, [onClickAddNewCriteriaActionId]);

  const handleMoveTo = useCallback(async () => {
    onClickMoveToActionId();
    const [separatorStopNumber, canceled] = await wrapPromise(
      stopMoveToModalRef.current.showModal({
        routeStops: preparedStops,
        selectedStops,
      })
    );

    if (canceled) return;

    moveSelectedStops({ separatorStopNumber });
  }, [preparedStops, selectedStops, moveSelectedStops, onClickMoveToActionId]);

  const handleTransferTo = useCallback(
    async (isStopsSelected) => {
      const [result = {}, canceled] = await wrapPromise(
        transferToModalRef.current.showModal({
          isStopsSelected,
        })
      );
      if (canceled) return;
      const { optimise, transferOnly } = result;
      !transferOnly
        ? startScheduleRoute(optimise)
        : openSummaryModal({ isTransfer: true });
    },
    [startScheduleRoute, openSummaryModal]
  );

  const mergeDisabled = useMemo(
    () => isMergeDisabled(selectedStops, routeStatusInBuild),
    [selectedStops, routeStatusInBuild]
  );

  const splitDisabled = useMemo(
    () => isSplitDisabled(selectedStops, routeStatusInBuild, selectedCount),
    [selectedStops, routeStatusInBuild, selectedCount]
  );

  return (
    <>
      <RouteManagement
        tableProps={parcelDetailsTableProps}
        selectedStops={selectedStops}
        clearSelection={showOverlayWhile(clearSelectedResetOrder)}
        handleRowClick={handleParcelSelect}
        onSelectMultipleClick={onSelectMultipleClick}
        handleBackClick={showOverlayWhile(handleBackClick)}
        isScheduleRequired={scheduleRequired}
        isBulkDisabled={routeStatusInBuild}
        isSplitDisabled={splitDisabled}
        isTransferDisabled={isParcelTransferDisabled}
        onChangeBulk={showOverlayWhile(onChangeBulk)}
        isRouteReadyOrOpen={routeReadyOrOpen}
        onMergeClick={showOverlayWhile(handleOnMergeClick)}
        onSplitClick={showOverlayWhile(handleOnSplitClick)}
        selectedCriteria={criteria}
        onClickAddNewCriteria={showOverlayWhile(handleSelectByCriteria)}
        onRemoveCriteria={onRemoveCriteria}
        onScheduleClick={onScheduleClick}
        onOptimiseClick={showOverlayWhile(onOptimiseClick)}
        onMoveToClick={handleMoveTo}
        onTransferToClick={handleTransferTo}
        isMoveToDisabled={isMoveToDisabled}
        onSaveClick={onSaveClick}
        isSaveRouteEnabled={saveRouteEnabled}
        isMergeDisabled={mergeDisabled}
        isStopTransferDisabled={isStopTransferDisabled}
        onRemoveClick={handleOnRemoveClick}
        isRemoveDisable={isRemoveDisabled}
      />
      <ModalControlled
        ref={mergeStopsModalRef}
        Component={MergeStopsModal}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={splitStopModalRef}
        Component={SplitStopModal}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={selectByCriteriaModalRef}
        Component={SelectByCriteriaModal}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        Component={StopMoveTo}
        ref={stopMoveToModalRef}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={transferToModalRef}
        Component={TransferTo}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={summaryModalRef}
        Component={TransferSummary}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={removeParcelsModalRef}
        Component={RemoveParcels}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
    </>
  );
};
const mapStateToProps = (state) => {
  const { criteria } = getStopFilters(state);
  return {
    scheduleRequired: isScheduleRequired(state),
    selectedCount: getSelectedParcelsCount(state),
    selectedStops: getSelectedPreparedStops(state),
    isKingDisabled: isKingColumnDisabled(state),
    criteria,
    isMoveToDisabled: getMoveToButtonDisabledState(state),
    isStopTransferDisabled: getStopTransferDisabledState(state),
    isParcelTransferDisabled: getParcelTransferDisabledState(state),
    isRemoveDisabled: getRemoveParcelsDisabledState(state),
    saveRouteEnabled: isSaveRouteEnabled(state),
    routeStatusInBuild: isRouteStatusInBuild(state),
    preparedStops: getPreparedStops(state),
    routeReadyOrOpen: isRouteReadyOrOpen(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(
    pick(
      [
        "clearSelectedResetOrder",
        "handleParcelSelect",
        "setCriteria",
        "removeCriteria",
      ],
      routeDetailActions
    ),
    dispatch
  ),
  onChangeBulk: () => dispatch(onChangeBulkStop()),
  onScheduleClick: async () => await dispatch(startScheduleRoute(false)),
  onOptimiseClick: async () => await dispatch(startScheduleRoute(true)),
  setScheduleRequired: async () => await dispatch(setScheduleRequired(true)),
  onSaveClick: () => dispatch(saveRoute()),
  onRemoveCriteria: (id) => {
    dispatch(routeDetailActions.resetSelectedParcels());
    dispatch(routeDetailActions.removeCriteria(id));
  },
  showNotificationMessage: () =>
    dispatch(
      showNotification({
        key: "no-stops-by-criteria-message",
        type: NotificationType.ACTION,
        message: "No stops match the selected criteria",
        buttonText: DISMISS,
      })
    ),
  moveSelectedStops: (separatorStopNumber) =>
    dispatch(moveSelectedStops(separatorStopNumber)),
  startScheduleRoute: (optimise) => dispatch(startScheduleRoute(optimise)),
  onUpdateKingParcel: (value) => dispatch(updateKingParcel(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTrackProps({
    handleParcelSelect: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.SELECT_PARCEL_ROW, { value }),
    onUpdateKingParcel: STOP_PARCELS_LIST.SELECT_KING_PARCEL,
    onChangeBulk: STOP_PARCELS_LIST.SELECT_BULK,
    onParcelRequestClick: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.CLICK_REQUEST, { value }),
    onSplitClickActionId: STOP_PARCELS_LIST.CLICK_SPLIT,
    onParcelNumberClick: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.CLICK_PARCEL_NUMBER, { value }),
    onClickAddNewCriteriaActionId: ROUTE_MANAGEMENT.CLICK_ADD_NEW_CRITERIA,
    handleBackClick: ROUTE_MANAGEMENT.CLICK_BACK,
    clearSelectedResetOrder: ROUTE_MANAGEMENT.CLICK_CLEAR_SELECTED,
    onMergeClickActionId: ROUTE_MANAGEMENT.CLICK_MERGE,
    onClickMoveToActionId: ROUTE_MANAGEMENT.CLICK_MOVE_TO,
    onOptimiseClick: ROUTE_MANAGEMENT.CLICK_OPTIMISE,
    onRemoveCriteria: (value) =>
      createLogEntryPayload(ROUTE_MANAGEMENT.CLICK_REMOVE_CRITERIA, { value }),
    onSaveClick: ROUTE_MANAGEMENT.CLICK_SAVE,
    onScheduleClick: ROUTE_MANAGEMENT.CLICK_SCHEDULE,
    onSelectMultipleClick: ROUTE_MANAGEMENT.CLICK_SELECT_MULTIPLE,
    handleOnLoad: ROUTE_MANAGEMENT.LOAD,
  })
)(RouteManagementContainer);
